/**
 * Theme provider: light / dark toggle with localStorage persistence
 * and a sensible initial value.
 *
 * The initial theme resolution order is:
 *   1. `localStorage.theme` if set (user's explicit choice on a prior visit)
 *   2. `prefers-color-scheme: dark` media query on the OS
 *   3. 'light' as a hard default
 *
 * On mount we mirror the resolved theme onto `<html class="dark">` or
 * no class so Tailwind's `dark:` variant picks it up via the
 * `@custom-variant dark (&:is(.dark *))` declaration in globals.css.
 *
 * NOTE: the script in index.html runs BEFORE hydration to set the
 * class, so there's no flash-of-wrong-theme on first paint. See
 * `themeInitScript` below — injected by __root.tsx.
 */

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const STORAGE_KEY = 'marketing-theme'

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // localStorage can throw in privacy-mode / embedded contexts.
  }
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark'
  return 'light'
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.style.colorScheme = theme
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Start on 'light' so the server render and the first client render agree; the stored or OS
  // theme is read after hydration. Reading it in the initial state made the toggle icons mismatch.
  const [theme, setThemeState] = useState<Theme>('light')
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setThemeState(readInitialTheme())
    setHydrated(true)
  }, [])

  useEffect(() => {
    if (!hydrated) return
    applyTheme(theme)
    try {
      window.localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // ignore
    }
  }, [theme, hydrated])

  // Respond to OS-level theme changes, but only if the user has not
  // set an explicit choice — that's the Apple / GNOME convention.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (e: MediaQueryListEvent) => {
      const stored = window.localStorage.getItem(STORAGE_KEY)
      if (stored === 'light' || stored === 'dark') return
      setThemeState(e.matches ? 'dark' : 'light')
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const setTheme = useCallback((next: Theme) => setThemeState(next), [])
  const toggleTheme = useCallback(() => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')), [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}

/**
 * Injected into the document <head> by __root.tsx so the initial
 * theme class is set BEFORE React hydrates. Without this, users on
 * dark mode would briefly see the light theme on first paint.
 */
export const themeInitScript = `
  (function () {
    try {
      var t = localStorage.getItem('${STORAGE_KEY}');
      if (t !== 'light' && t !== 'dark') {
        t = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
      }
      document.documentElement.classList.toggle('dark', t === 'dark');
      document.documentElement.style.colorScheme = t;
    } catch (_) {}
  })();
`
